import React, { useState } from "react";
import { Flex, ModalV2, Select, Textarea } from "@jsq/ds/core";
import { SPACING } from "#utils/spacing";
import * as styles from "../styles_vanilla.css";

interface ReturnToFundAdminModalProps {
  isOpen: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (rejectionReason: string, note: string) => void;
}

// Structured rejection reasons surfaced when a GP returns an external review
// to the fund admin. Coded values are persisted on the approval row so
// dashboards can render them without parsing the free-text note.
export const REJECTION_REASONS: { value: string; label: string }[] = [
  { value: "TYPO_FORMATTING", label: "Typo/Formatting" },
  { value: "INCORRECT_ENTITY_ACCOUNT_LABEL", label: "Incorrect Entity/Account Label" },
  { value: "CALCULATION_MATH_DISCREPANCY", label: "Calculation/Math Discrepancy" },
  { value: "MISSING_SUPPORTING_DOCUMENTATION", label: "Missing Supporting Documentation" },
  {
    value: "INVESTOR_ALLOCATION_LP_STATEMENT_ERROR",
    label: "Investor Allocation/LP Statement Error",
  },
  { value: "NAV_CALCULATION_ERROR", label: "NAV Calculation Error" },
  {
    value: "WIRE_CASH_MOVEMENT_INSTRUCTION_ERROR",
    label: "Wire/Cash Movement Instruction Error",
  },
  { value: "CLIENT_CAUSED_ERROR", label: "Client Caused Error" },
  { value: "OTHER", label: "Other" },
];

export const getRejectionReasonLabel = (value: string): string =>
  REJECTION_REASONS.find((r) => r.value === value)?.label ?? value;

export const ReturnToFundAdminModal: React.FC<ReturnToFundAdminModalProps> = ({
  isOpen,
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [note, setNote] = useState("");

  const reset = () => {
    setRejectionReason("");
    setNote("");
  };

  const handleHide = () => {
    reset();
    onCancel();
  };

  const handleSubmit = () => {
    onConfirm(rejectionReason, note);
    reset();
  };

  const noteRequired = rejectionReason === "OTHER";
  const confirmDisabled = loading || !rejectionReason || (noteRequired && !note.trim());

  return (
    <ModalV2
      show={isOpen}
      onHide={handleHide}
      title="Return to Fund Admin"
      size="medium"
      data-testid="return-to-fund-admin-modal"
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.LARGE}>
          <div>
            <div className={styles.modalLabel}>Reason for return</div>
            <Select
              value={rejectionReason}
              onChange={(v) => setRejectionReason(String(v))}
              full
              inModal
              label="Reason for return"
              hideLabel
              placeholder="Select a reason"
              data-testid="return-to-fund-admin-reason"
            >
              <Select.Section>
                {REJECTION_REASONS.map((r) => (
                  <Select.Option key={r.value} value={r.value}>
                    {r.label}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
          </div>

          <div>
            <Textarea
              name="return-to-fund-admin-note"
              label="Note"
              value={note}
              onSetValue={setNote}
              placeholder="Please specify any additional feedback"
              isFullWidth
              size="medium"
            />
            <div className={styles.modalNoteHint}>{noteRequired ? "Required" : "Optional"}</div>
          </div>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL} className={styles.modalFooter}>
          <button
            className={styles.actionButton}
            onClick={handleHide}
            disabled={loading}
            data-testid="return-to-fund-admin-cancel"
          >
            Cancel
          </button>
          <button
            className={styles.actionButtonPrimary}
            onClick={handleSubmit}
            disabled={confirmDisabled}
            data-testid="return-to-fund-admin-confirm"
          >
            Return to Fund Admin
          </button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default ReturnToFundAdminModal;
